import { HttpClient } from '@adapters/HttpClient';
import {
  BankAccountResponse,
  BankAccountsAdvancedFilters,
  BankAccountsFilters,
  BankAccountsResponse,
  CreateBankAccountPayload,
  PayerBanksFilters,
  PayerBanksResponse,
  RejectBankAccountPayload,
  VerifyBankAccountPayload,
} from '../models/BankAccount';

const buildAdvancedFiltersParams = (advancedFilters: BankAccountsAdvancedFilters) => {
  const params: Record<string, string | number> = {};

  advancedFilters.filters.forEach((filter, index) => {
    params[`advanced_filters[filters][${index}][column]`] = filter.column;
    params[`advanced_filters[filters][${index}][operator]`] = filter.operator;

    if (
      filter.value === undefined ||
      filter.value === null ||
      (Array.isArray(filter.value) && filter.value.length === 0)
    ) {
      return;
    }

    if (Array.isArray(filter.value)) {
      filter.value.forEach((value, valueIndex) => {
        params[`advanced_filters[filters][${index}][value][${valueIndex}]`] = value;
      });
      return;
    }

    params[`advanced_filters[filters][${index}][value]`] = filter.value;
  });

  if (advancedFilters.logic) {
    params['advanced_filters[logic]'] = advancedFilters.logic;
  }

  return params;
};

export class BankAccountsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * GET /bank_accounts - Lista de cuentas bancarias
   */
  async getBankAccounts(filters: BankAccountsFilters = {}): Promise<BankAccountsResponse> {
    const { page = 1, perPage = 25, team_ids, advanced_filters, sorting } = filters;

    const sortColumn = sorting?.column ?? 'id';
    const sortDirection = sorting?.direction ?? 'desc';

    const params: Record<string, string | number> = {
      page,
      per_page: perPage,
      'sorting[column]': sortColumn,
      'sorting[direction]': sortDirection,
    };

    if (team_ids?.trim()) {
      params.team_ids = team_ids.trim();
    }

    if (advanced_filters && advanced_filters.filters.length > 0) {
      Object.assign(
        params,
        buildAdvancedFiltersParams({
          logic: advanced_filters.logic ?? 'AND',
          filters: advanced_filters.filters,
        }),
      );
    }

    return await this.httpClient.get<BankAccountsResponse>('/bank_accounts', params, {}, true);
  }

  /**
   * GET /bank_accounts/{id} - Detalle de cuenta bancaria
   */
  async getBankAccount(id: number): Promise<BankAccountResponse> {
    return await this.httpClient.get<BankAccountResponse>(`/bank_accounts/${id}`, {}, {}, true);
  }

  /**
   * POST /bank_accounts - Crear cuenta bancaria
   */
  async createBankAccount(payload: CreateBankAccountPayload): Promise<BankAccountResponse> {
    const formData = new FormData();

    formData.append('holder_name', payload.holder_name);
    formData.append('iban', payload.iban);
    formData.append('bic', payload.bic);
    formData.append('bank_name', payload.bank_name);
    formData.append('country_code', payload.country_code);
    formData.append('currency', payload.currency);

    if (payload.internal_note) {
      formData.append('internal_note', payload.internal_note);
    }

    if (payload.proof_document_file) {
      formData.append('proof_document_file', payload.proof_document_file);
    }

    return await this.httpClient.post<BankAccountResponse>('/bank_accounts', formData, true, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  /**
   * GET /payer-banks - Bancos pagadores del equipo
   */
  async getPayerBanks(filters: PayerBanksFilters = {}): Promise<PayerBanksResponse> {
    const { page = 1, perPage = 25, sorting } = filters;
    const sortColumn = sorting?.column ?? 'id';
    const sortDirection = sorting?.direction ?? 'asc';
    const params: Record<string, string | number> = {
      page,
      per_page: perPage,
      'sorting[column]': sortColumn,
      'sorting[direction]': sortDirection,
    };
    return await this.httpClient.get<PayerBanksResponse>('/payer-banks', params, {}, true);
  }

  /**
   * PUT /bank_accounts/{id}/verify - Verificar cuenta bancaria
   */
  async verifyBankAccount(id: number, payload: VerifyBankAccountPayload): Promise<BankAccountResponse> {
    return await this.httpClient.put<BankAccountResponse>(`/bank_accounts/${id}/verify`, payload);
  }

  /**
   * PUT /bank_accounts/{id}/reject - Rechazar cuenta bancaria
   */
  async rejectBankAccount(id: number, payload: RejectBankAccountPayload): Promise<BankAccountResponse> {
    return await this.httpClient.put<BankAccountResponse>(`/bank_accounts/${id}/reject`, payload);
  }

  /**
   * PUT /bank_accounts/{id}/disable - Deshabilitar cuenta bancaria
   */
  async disableBankAccount(id: number): Promise<BankAccountResponse> {
    return await this.httpClient.put<BankAccountResponse>(`/bank_accounts/${id}/disable`, {});
  }
}
